<script>
	import { onMount, onDestroy } from "svelte";
	import { fade } from "svelte/transition";
	import { soundImagesGen2All as all } from "../../stores";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { ErrorCodes } from "../../define/define";
	import {
		getApi,
		getSoundImagesGen2List,
		uploadSoundImageGen2,
		deleteSoundImageGen2,
		refreshSoundImageGen2Cache,
	} from "../../service/api";
	import { getHRLevelText, getHRLevelBadgeColor, handleImageError } from "../../utils/musicCommon";
	import { getResourceUrl } from "../../utils/resource";
	import { playAudio, stopAudio } from "../../utils/audio";
	import { Dates } from "../../utils/date";
	import { blurHashToDataUrl } from "../../utils/blurhash";
	import SoundImageFeedback from "../../components/SoundImageFeedback.svelte";

	// 1차 분류 (음원 타입)
	const TYPE_OPTIONS = [
		{ value: "sleep", text: "수면", categoryType: 1 },
		{ value: "meditation", text: "명상", categoryType: 2 },
		{ value: "focus", text: "포커스", categoryType: 3 },
	];

	// 정렬 옵션 — 목록 응답에 실제로 들어오는 필드만 노출한다.
	// (음원 자체의 생성/수정일은 목록에 없어 제외. 수정일순 = 이미지 등록·교체 일시)
	const SORT_OPTIONS = [
		{ value: "updated", text: "수정일순" },
		{ value: "title", text: "제목순" },
		{ value: "category", text: "카테고리순" },
		{ value: "source", text: "소스명순" },
		{ value: "active", text: "노출상태순" },
		{ value: "level", text: "레벨순" },
	];

	// 업로드 허용 MIME (백엔드 검사와 동일하게 유지)
	const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

	// 기존 컨텐츠 음원 화면과 동일하게 전체를 한 번에 받아 클라이언트에서 필터링한다.
	// 그래야 2차 분류·미등록 필터·진행률이 페이지가 아닌 전체 기준으로 동작한다.
	const FETCH_SIZE = 1000;

	// 목록 상태
	let items = [];
	let categories = [];
	let currentType = "sleep";
	let activeCategory = null; // 2차 분류. null 이면 전체
	let searchKeyword = "";
	let isLoading = false;
	let showOnlyMissing = false;

	// 정렬 상태 — 이미지 작업 화면이라 기본은 "가장 최근에 손댄 것 먼저"
	let currentSort = "updated";
	let currentSortDirection = "desc";

	// 타일 hover 상태 — 이 프로젝트는 컴포넌트 <style> 이 번들에 로드되지 않아
	// :hover 를 쓸 수 없다. Svelte 상태로 대체한다.
	let hoveredId = null;

	// 업로드 모달 상태
	let showUploadModal = false;
	let uploadTarget = null;
	let selectedImageFile = null;
	let imagePreviewUrl = "";
	let isSaving = false;
	let isCaching = false;

	// 상세보기 모달 상태
	let showDetailModal = false;
	let detailItem = null; // 목록 행 (images 포함)
	let detailInfo = null; // /sound/music/gen2/{id} 응답 (음원 메타)
	let isDetailLoading = false;
	let detailUnresolvedCount = 0; // 피드백 컴포넌트가 올려주는 미해결 건수


	// 이미지 원본보기 모달 상태
	let lightboxUrl = "";
	let lightboxTitle = "";
	let lightboxCaption = "";
	let lightboxHasSource = true; // data URL 이면 false — 새 탭 열기를 숨긴다
	// 원본 픽셀 크기(1:1)로 열지, 화면에 맞춰 줄일지.
	// 이미지가 실제로 몇 픽셀짜리인지 눈으로 확인하는 화면이라 1:1 이 기본이다.
	// 화면보다 크면 상자 안에서 스크롤한다.
	let lightboxIsActualSize = true;

	// 음원 미리듣기 상태
	let audioPlayer = null;
	let isPlaying = false;

	// 현재 1차 분류에 속한 2차 분류 목록
	$: currentCategoryType = TYPE_OPTIONS.find((option) => option.value === currentType)?.categoryType;
	$: typeCategories = categories.filter((category) => category.type === currentCategoryType);

	// 2차 분류 + 미등록 필터 적용 결과
	$: categoryFiltered =
		activeCategory === null ? items : items.filter((item) => item.idCategory === activeCategory);
	$: visibleItems = showOnlyMissing ? categoryFiltered.filter((item) => !item.images) : categoryFiltered;

	// 정렬 비교. 항상 오름차순 기준으로 반환하고 방향은 sortItems 에서 곱한다.
	// 미등록 음원은 이미지 수정일이 없어 "" 로 취급되므로 수정일순 내림차순에서 맨 뒤로 밀린다.
	const compareItems = (a, b, sortKey) => {
		switch (sortKey) {
			case "updated":
				return (a.images?.updatedAt || "").localeCompare(b.images?.updatedAt || "");
			case "title":
				return (a.subjectKo || "").localeCompare(b.subjectKo || "", "ko");
			case "category":
				return (a.categoryNameKo || "").localeCompare(b.categoryNameKo || "", "ko");
			case "source":
				return (a.sourceSubject || "").localeCompare(b.sourceSubject || "", "ko");
			case "active":
				return (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0);
			case "level":
				return (a.level || 0) - (b.level || 0);
			default:
				return 0;
		}
	};

	const sortItems = (list, sortKey, sortDirection) => {
		const direction = sortDirection === "asc" ? 1 : -1;
		return [...list].sort((a, b) => {
			const result = compareItems(a, b, sortKey);
			// 값이 같으면 id 로 고정해 매번 같은 순서가 나오도록 한다
			return result !== 0 ? direction * result : a.id - b.id;
		});
	};

	$: sortedItems = sortItems(visibleItems, currentSort, currentSortDirection);

	// blurHash 미리보기 — 상세보기가 열려 있는 동안만 디코딩한다.
	// 값이 잘못됐거나 규격에 안 맞으면 빈 문자열이 와서 아래 마크업이 "미리보기 불가" 로 떨어진다.
	//
	// 디코딩 해상도는 32x32 로 고정해도 된다. blurHash 의 복원식은 좌표를 폭·높이로 정규화해
	// 쓰기 때문에 어떤 해상도로 풀든 같은 그림이 나오고, 종횡비는 표시 상자가 정한다.
	$: blurHashPreviewUrl = detailItem?.images?.blurHash ? blurHashToDataUrl(detailItem.images.blurHash) : "";

	// 원본 픽셀 크기는 업로드 시점에 sound_images_gen2 에 적재된 값을 그대로 쓴다.
	// 이미지를 받아서 재는 방식은 앱이 못 쓴다 — 앱은 원본을 내려받기 전에 플레이스홀더를
	// 그려야 하므로, 크기는 처음부터 DB 에 있어야 하는 값이다. 어드민도 같은 값을 본다.
	// 값이 0 이면 컬럼 추가 전에 등록된 건이라 재업로드 전까지 비율을 알 수 없다.
	$: imageWidth = detailItem?.images?.width || 0;
	$: imageHeight = detailItem?.images?.height || 0;

	// 업로드 원본 — 사용자가 올린 파일 그대로(sourceUrl). 앱에는 내려가지 않는다.
	// 백엔드가 어드민 응답에만 omitempty 로 실어 보내고 v2 캐시에서는 빼므로,
	// 값이 없으면 원본 보관 도입 전에 등록된 건이다.
	$: sourceUrl = detailItem?.images?.sourceUrl || "";

	// 포맷은 별도 필드가 없어 저장 확장자로 판별한다.
	// 백엔드가 파일 시그니처로 판정한 포맷에 맞춰 확장자를 붙이므로 신뢰할 수 있다.
	$: sourceFormat = (sourceUrl.split("?")[0].split(".").pop() || "").toUpperCase();

	// 파일 용량 (bytes). 업로드 시점에 적재된 값을 쓴다.
	// 원파일 보관이 붙으면서 음원 1건당 저장량이 크게 늘었기 때문에,
	// 어느 건이 과하게 무거운지 화면에서 바로 걸러낼 수 있어야 한다.
	// 값이 0 이면 용량 컬럼 추가 전에 등록된 건이다.
	$: imageSize = detailItem?.images?.imageSize || 0;
	$: thumbnailSize = detailItem?.images?.thumbnailSize || 0;
	$: sourceSize = detailItem?.images?.sourceSize || 0;
	$: totalSize = imageSize + thumbnailSize + sourceSize;

	// 바이트 표기. 값이 없으면 빈 문자열이라 호출부에서 표기 자체가 빠진다.
	const formatBytes = (bytes) => {
		if (!bytes) return "";
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	// 행 부제를 " · " 로 잇는다. 아직 안 내려오는 값은 자동으로 빠진다.
	const joinMeta = (...parts) => parts.filter(Boolean).join(" · ");

	// blurHash 는 종횡비 정보를 담지 않는다. 원본을 가리는 플레이스홀더이므로
	// 원본과 같은 비율(세로 이미지면 세로)로 그려야 앱에서 보이는 모습과 일치한다.
	// 크기를 모르면 정사각으로 두고, 극단적인 비율은 상자가 깨지지 않게 자른다.
	const PREVIEW_BOX_HEIGHT = 64;
	$: blurHashBoxWidth =
		imageHeight > 0
			? Math.min(128, Math.max(24, Math.round((PREVIEW_BOX_HEIGHT * imageWidth) / imageHeight)))
			: PREVIEW_BOX_HEIGHT;

	// 진행 현황 (2차 분류까지 반영한 기준)
	$: registeredCount = categoryFiltered.filter((item) => item.images).length;
	$: missingCount = categoryFiltered.length - registeredCount;
	$: progressPercent = categoryFiltered.length > 0 ? Math.round((registeredCount / categoryFiltered.length) * 100) : 0;

	// 2차 분류 정보 조회
	const loadCategories = async () => {
		const response = await getApi("/contents/category/gen2");
		if (response.resultCode === ErrorCodes.OK) {
			categories = response.data || [];
		}
	};

	// 목록 조회
	const loadList = async () => {
		isLoading = true;
		try {
			const url = getSoundImagesGen2List(FETCH_SIZE, 1, currentType, searchKeyword);
			await all.fetch(url);
			items = $all.data?.list || [];
		} finally {
			isLoading = false;
		}
	};

	// 1차 분류 변경 핸들러
	const handleTypeChange = (type) => {
		if (currentType === type) return;
		currentType = type;
		activeCategory = null; // 타입이 바뀌면 2차 분류는 전체로 되돌린다
		loadList();
	};

	// 2차 분류 변경 핸들러
	const handleCategoryChange = (categoryId) => {
		activeCategory = categoryId;
	};

	// 검색 핸들러
	const handleSearch = () => {
		loadList();
	};

	// 검색창 엔터 핸들러
	const handleSearchKeydown = (event) => {
		if (event.key === "Enter") {
			handleSearch();
		}
	};

	// 미리보기 objectURL 해제 (메모리 누수 방지)
	const revokeImagePreview = () => {
		if (imagePreviewUrl) {
			URL.revokeObjectURL(imagePreviewUrl);
			imagePreviewUrl = "";
		}
	};

	// 재생 정지
	const stopPreview = () => {
		if (isPlaying) {
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
		}
	};

	// 업로드 모달 열기 — 타일을 누르면 바로 열린다
	const openUploadModal = (item) => {
		uploadTarget = item;
		revokeImagePreview();
		selectedImageFile = null;
		showUploadModal = true;
	};

	// 업로드 모달 닫기
	const closeUploadModal = () => {
		showUploadModal = false;
		uploadTarget = null;
		revokeImagePreview();
		selectedImageFile = null;
	};

	// 이미지 파일 선택 핸들러
	const handleImageSelect = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			alert("PNG, JPEG, WebP 이미지만 업로드할 수 있습니다.");
			event.target.value = "";
			return;
		}

		revokeImagePreview();
		selectedImageFile = file;
		imagePreviewUrl = URL.createObjectURL(file);
	};

	// 이미지 업로드 (등록/교체)
	const uploadImage = async () => {
		if (!selectedImageFile || !uploadTarget) {
			alert("이미지 파일을 선택하세요.");
			return;
		}

		const targetId = uploadTarget.id;
		isSaving = true;
		try {
			const formData = new FormData();
			formData.append("idSound", targetId);
			formData.append("imageFile", selectedImageFile);

			const result = await uploadSoundImageGen2(formData);
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "이미지 업로드에 실패했습니다.");
				return;
			}

			// 목록을 다시 부르지 않고 해당 타일만 갱신한다.
			items = items.map((item) => (item.id === targetId ? { ...item, images: result.data.images } : item));
			if (detailItem && detailItem.id === targetId) {
				detailItem = { ...detailItem, images: result.data.images };
			}
			closeUploadModal();
		} catch (error) {
			alert("이미지 업로드 중 오류가 발생했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 이미지 삭제
	const deleteImage = async (item) => {
		if (!confirm(`"${item.subjectKo}" 의 이미지를 삭제하시겠습니까?\n삭제하면 캐시 갱신 시 앱 목록에서 이 음원이 빠집니다.`)) {
			return;
		}

		try {
			const result = await deleteSoundImageGen2(item.id);
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "이미지 삭제에 실패했습니다.");
				return;
			}

			items = items.map((row) => (row.id === item.id ? { ...row, images: null } : row));
			if (detailItem && detailItem.id === item.id) {
				detailItem = { ...detailItem, images: null };
			}
		} catch (error) {
			alert("이미지 삭제 중 오류가 발생했습니다.");
		}
	};

	// 상세보기 열기 — 음원 메타는 기존 단건 API 에서 가져온다
	const openDetailModal = async (item) => {
		detailItem = item;
		detailInfo = null;
		showDetailModal = true;
		isDetailLoading = true;
		try {
			const response = await getApi(`/sound/music/gen2/${item.id}`);
			if (response.resultCode === ErrorCodes.OK) {
				detailInfo = response.data;
			}
		} finally {
			isDetailLoading = false;
		}
	};

	// 피드백 컴포넌트가 올려주는 집계 — 모달 헤더 배지에 쓴다
	const handleFeedbackCountChange = (event) => {
		detailUnresolvedCount = event.detail.unresolvedCount || 0;
	};

	// 상세보기 닫기
	const closeDetailModal = () => {
		stopPreview();
		showDetailModal = false;
		detailItem = null;
		detailInfo = null;
		detailUnresolvedCount = 0;
	};

	// 라이트박스 열기.
	// hasSource 가 false 면 data URL 이라 "새 탭에서 열기" 를 숨긴다.
	// 브라우저가 data: 로의 최상위 이동을 막아서 눌러도 아무 일이 안 일어나기 때문이다.
	const showLightbox = ({ url, title, caption, hasSource = true }) => {
		if (!url) return;
		lightboxUrl = url;
		lightboxTitle = title || "";
		lightboxCaption = caption || "";
		lightboxHasSource = hasSource;
		lightboxIsActualSize = true;
	};

	// 전체 이미지 보기 — 앱이 큰 화면에 쓰는 WebP.
	// 업로드 원본(raw) 과는 다른 파일이다. 아래 openSourceLightbox 참고.
	const openLightbox = (item) => {
		showLightbox({
			url: item.images?.imageUrl,
			title: `${item.subjectKo || ""} — 전체 이미지`,
			caption: "앱이 큰 화면에 쓰는 이미지입니다.",
		});
	};

	// 업로드 원본 보기 — 변환 전 파일 그대로
	const openSourceLightbox = (item) => {
		if (!item.images?.sourceUrl) return;
		showLightbox({
			url: item.images.sourceUrl,
			title: `${item.subjectKo || ""} — 원본 이미지`,
			caption: "업로드한 파일 그대로입니다. 앱에는 내려가지 않습니다.",
		});
	};

	// 썸네일 보기
	const openThumbnailLightbox = (item) => {
		showLightbox({
			url: item.images?.thumbnailUrl,
			title: `${item.subjectKo || ""} — 썸네일`,
			caption: "목록·그리드 타일에 쓰는 이미지입니다.",
		});
	};

	// blurHash 보기.
	// 목록용 32px 미리보기를 그대로 확대하면 뭉개지므로 이때만 크게 다시 푼다.
	// 원본 비율을 알면 그 비율로, 아직 못 쟀으면 정사각으로 푼다.
	const openBlurHashLightbox = (item) => {
		const hash = item.images?.blurHash;
		if (!hash) return;

		const longEdge = 320;
		let width = longEdge;
		let height = longEdge;
		if (imageWidth > 0 && imageHeight > 0) {
			if (imageWidth >= imageHeight) {
				height = Math.max(1, Math.round((longEdge * imageHeight) / imageWidth));
			} else {
				width = Math.max(1, Math.round((longEdge * imageWidth) / imageHeight));
			}
		}

		showLightbox({
			url: blurHashToDataUrl(hash, width, height),
			title: `${item.subjectKo || ""} — blurHash`,
			caption: `앱이 원본을 내려받는 동안 이 자리에 깔리는 플레이스홀더입니다. · ${hash}`,
			hasSource: false,
		});
	};

	// 라이트박스 닫기
	const closeLightbox = () => {
		lightboxUrl = "";
		lightboxTitle = "";
		lightboxCaption = "";
		lightboxHasSource = true;
		lightboxIsActualSize = true;
	};

	// 음원 미리듣기 — 이미지가 음원에 어울리는지 들으면서 판단한다
	const handlePreviewAudio = () => {
		if (isPlaying) {
			stopPreview();
			return;
		}
		if (!detailInfo?.fileName) {
			alert("재생할 음원이 없습니다.");
			return;
		}
		playAudio(
			detailInfo.fileName,
			{},
			(v) => (audioPlayer = v),
			(v) => (isPlaying = v),
			getResourceUrl
		);
	};

	// v2 캐시 갱신
	const refreshCache = async () => {
		if (!confirm("v2 캐시를 갱신하시겠습니까?\n이미지가 등록되지 않은 음원은 앱 목록에서 제외됩니다.")) {
			return;
		}

		isCaching = true;
		try {
			const result = await refreshSoundImageGen2Cache();
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "캐시 갱신에 실패했습니다.");
				return;
			}

			// 이미지 미등록으로 제외된 건수를 알려줘야 남은 작업을 인지할 수 있다.
			const cached = result.data?.cachedCount ?? 0;
			const excluded = result.data?.excludedCount ?? 0;
			alert(
				excluded > 0
					? `v2 캐시가 갱신되었습니다.\n반영 ${cached}건 / 이미지 미등록 ${excluded}건 제외`
					: `v2 캐시가 갱신되었습니다.\n반영 ${cached}건 (제외 없음)`
			);
		} catch (error) {
			alert("캐시 갱신 중 오류가 발생했습니다.");
		} finally {
			isCaching = false;
		}
	};

	onMount(async () => {
		menu.set("컨텐츠 관리");
		menuSub.set("음원 관리");
		menuSubSub.set("음원 이미지 (2세대)");
		await loadCategories();
		await loadList();
	});

	onDestroy(() => {
		revokeImagePreview();
		stopPreview();
	});
</script>

<div class="card">
	<div class="p-3 pb-0">
		<!-- 1차 분류 (음원 타입) — 기존 컨텐츠 음원 화면과 동일한 탭 스타일 -->
		<ul class="nav nav-tabs d-flex mb-2" role="tablist">
			{#each TYPE_OPTIONS as typeOption}
				<li class="nav-item">
					<button
						class="nav-link {currentType === typeOption.value
							? 'active border-top border-start border-end border-secondary'
							: ''}"
						style="cursor: pointer;"
						on:click={() => handleTypeChange(typeOption.value)}
					>
						{typeOption.text}
					</button>
				</li>
			{/each}
		</ul>

		<!-- 2차 분류 (컨텐츠 장르) + 우측 컨트롤 -->
		<ul class="nav nav-tabs nav-tabs-sm mb-2 d-flex align-items-center" role="tablist" style="min-height: 36px;">
			<li class="nav-item">
				<a
					class="nav-link {activeCategory === null ? 'active' : ''}"
					on:click|preventDefault={() => handleCategoryChange(null)}
					href="#category-all"
					role="tab"
					style="height: 28px; display: flex; align-items: center; font-size: 0.75rem; padding: 0.2rem 0.4rem;"
				>
					전체
				</a>
			</li>
			{#each typeCategories as category}
				<li class="nav-item">
					<a
						class="nav-link {activeCategory === category.id ? 'active' : ''}"
						on:click|preventDefault={() => handleCategoryChange(category.id)}
						href="#category-{category.id}"
						role="tab"
						style="height: 28px; display: flex; align-items: center; font-size: 0.75rem; padding: 0.2rem 0.4rem;"
					>
						{category.subjectKo}
					</a>
				</li>
			{/each}

			<div class="ms-auto d-flex align-items-center" style="padding: 0 5px;">
				<input
					type="text"
					class="form-control form-control-sm me-2"
					placeholder="제목 또는 소스명 검색"
					style="width: 200px; height: 28px; font-size: 0.8rem;"
					bind:value={searchKeyword}
					on:keydown={handleSearchKeydown}
				/>
				<button
					class="btn btn-sm btn-outline-primary me-2"
					style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
					on:click={handleSearch}
				>
					검색
				</button>
				<select
					class="form-select form-select-sm me-2"
					style="width: auto; height: 28px; font-size: 0.8rem;"
					bind:value={currentSort}
				>
					{#each SORT_OPTIONS as sortOption}
						<option value={sortOption.value}>{sortOption.text}</option>
					{/each}
				</select>
				<div class="btn-group me-2">
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="오름차순"
						style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
						on:click={() => (currentSortDirection = "asc")}
					>
						<i class="bx bx-sort-up" style="font-size: 0.9rem;"></i>
					</button>
					<button
						type="button"
						class="btn btn-sm {currentSortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}"
						title="내림차순"
						style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
						on:click={() => (currentSortDirection = "desc")}
					>
						<i class="bx bx-sort-down" style="font-size: 0.9rem;"></i>
					</button>
				</div>
				<button
					class="btn btn-sm btn-outline-primary me-1"
					style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
					on:click={loadList}
				>
					Reload
				</button>
				<button
					class="btn btn-sm btn-warning"
					style="height: 28px; display: flex; align-items: center; padding: 0.25rem 0.5rem;"
					on:click={refreshCache}
					disabled={isCaching}
				>
					{isCaching ? "갱신 중..." : "v2 캐시 갱신"}
				</button>
			</div>
		</ul>
	</div>

	<!-- 진행 현황 — 이미지를 다 채워야 하는 작업이라 남은 양이 항상 보이게 둔다 -->
	<div class="px-3 py-2 border-top border-bottom bg-light d-flex align-items-center gap-3 flex-wrap">
		<div class="d-flex align-items-center gap-2">
			<span class="fw-semibold small">{registeredCount}/{categoryFiltered.length} 등록</span>
			<div class="progress" style="width: 160px; height: 8px;">
				<div
					class="progress-bar {missingCount === 0 ? 'bg-success' : 'bg-warning'}"
					style="width: {progressPercent}%"
					role="progressbar"
					aria-valuenow={progressPercent}
					aria-valuemin="0"
					aria-valuemax="100"
				></div>
			</div>
			{#if missingCount > 0}
				<span class="badge bg-label-warning">미등록 {missingCount}건</span>
			{:else if categoryFiltered.length > 0}
				<span class="badge bg-label-success">모두 등록됨</span>
			{/if}
		</div>

		<div class="form-check form-switch mb-0">
			<input class="form-check-input" type="checkbox" id="onlyMissing" bind:checked={showOnlyMissing} />
			<label class="form-check-label small" for="onlyMissing">미등록만 보기</label>
		</div>

		<span class="ms-auto text-muted small">표시 {sortedItems.length}건</span>
	</div>

	<div class="card-body">
		{#if isLoading}
			<div class="text-center p-5 text-muted">
				<div class="spinner-border spinner-border-sm me-2" role="status"></div>
				불러오는 중...
			</div>
		{:else if sortedItems.length === 0}
			<div class="text-center p-5">
				<i class="bx {showOnlyMissing ? 'bx-check-circle' : 'bx-info-circle'} fs-1 text-muted"></i>
				<p class="text-muted mt-3">
					{showOnlyMissing ? "이미지가 모두 등록되어 있습니다." : "음원이 없습니다."}
				</p>
			</div>
		{:else}
			<!-- 커버아트를 판단하는 화면이라 이미지를 정사각형 타일로 크게 보여준다 -->
			<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 1rem;">
				{#each sortedItems as item, index (item.id)}
					<div in:fade={{ duration: 200, delay: Math.min(index * 15, 300) }}>
						{#if item.images?.thumbnailUrl}
							<!-- 등록됨: 이미지가 주인공. 올리면 작업 버튼이 덮인다 -->
							<div
								class="position-relative rounded overflow-hidden border"
								style="width: 100%; padding-top: 100%;"
								on:mouseenter={() => (hoveredId = item.id)}
								on:mouseleave={() => (hoveredId = null)}
							>
								<img
									src={item.images.thumbnailUrl}
									alt={item.subjectKo}
									class="position-absolute top-0 start-0 w-100 h-100"
									style="object-fit: cover;"
									on:error={handleImageError}
								/>

								{#if !item.isActive}
									<span class="badge bg-secondary position-absolute" style="top: 0.5rem; left: 0.5rem;">미노출</span>
								{/if}
								{#if getHRLevelText(item.level)}
									<span
										class="badge {getHRLevelBadgeColor(item.level)} position-absolute"
										style="top: 0.5rem; right: 0.5rem;"
									>
										{getHRLevelText(item.level)}
									</span>
								{/if}

								{#if hoveredId === item.id}
									<!-- 삭제는 실수 방지를 위해 상세보기에서만 제공한다 -->
									<div
										class="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2 p-2"
										style="background: rgba(0, 0, 0, 0.6);"
										transition:fade={{ duration: 100 }}
									>
										<button class="btn btn-sm btn-light w-100" on:click={() => openLightbox(item)}>
											<i class="bx bx-expand-alt me-1"></i>크게보기
										</button>
										<button class="btn btn-sm btn-light w-100" on:click={() => openUploadModal(item)}>
											<i class="bx bx-refresh me-1"></i>교체
										</button>
										<button class="btn btn-sm btn-outline-light w-100" on:click={() => openDetailModal(item)}>
											<i class="bx bx-detail me-1"></i>상세
										</button>
									</div>
								{/if}
							</div>
						{:else}
							<!-- 미등록: 점선 빈 슬롯. 타일 전체가 업로드 버튼이다 -->
							<div
								class="position-relative rounded"
								style="width: 100%; padding-top: 100%; border: 2px dashed #ffab00; background: rgba(255, 171, 0, 0.08); cursor: pointer;"
								on:click={() => openUploadModal(item)}
								on:keydown={(e) => e.key === "Enter" && openUploadModal(item)}
								role="button"
								tabindex="0"
								title="클릭해서 이미지 등록"
							>
								<div
									class="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
									style="color: #ffab00;"
								>
									<i class="bx bx-plus fs-2"></i>
									<span class="small mt-1">이미지 등록</span>
								</div>

								{#if !item.isActive}
									<span class="badge bg-secondary position-absolute" style="top: 0.5rem; left: 0.5rem;">미노출</span>
								{/if}
								{#if getHRLevelText(item.level)}
									<span
										class="badge {getHRLevelBadgeColor(item.level)} position-absolute"
										style="top: 0.5rem; right: 0.5rem;"
									>
										{getHRLevelText(item.level)}
									</span>
								{/if}
							</div>
						{/if}

						<div class="mt-2" style="font-size: 0.8rem; line-height: 1.3;">
							<div
								class="fw-semibold text-truncate"
								style="cursor: pointer; color: #0d6efd;"
								title="{item.subjectKo} — 클릭하면 상세보기"
								on:click={() => openDetailModal(item)}
								on:keydown={(e) => e.key === "Enter" && openDetailModal(item)}
								role="button"
								tabindex="0"
							>
								{item.subjectKo || "-"}
							</div>
							<div class="text-muted text-truncate" title={item.sourceSubject}>
								{item.categoryNameKo || "-"} · {item.sourceSubject || "-"}
							</div>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

<!-- 상세보기 모달 -->
{#if showDetailModal}
	<div class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5);" transition:fade={{ duration: 120 }}>
		<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						음원 이미지 상세
						<span class="text-muted small">#{detailItem?.id}</span>
						{#if detailUnresolvedCount > 0}
							<span class="badge bg-label-warning ms-2">미해결 피드백 {detailUnresolvedCount}</span>
						{/if}
					</h5>
					<button type="button" class="btn-close" on:click={closeDetailModal} aria-label="닫기"></button>
				</div>

				<div class="modal-body">
					<div class="row g-4">
						<!-- 이미지 영역 -->
						<div class="col-md-5">
							{#if detailItem?.images?.imageUrl}
								<div
									class="position-relative rounded overflow-hidden border"
									style="width: 100%; padding-top: 100%; cursor: zoom-in;"
									on:click={() => openLightbox(detailItem)}
									on:keydown={(e) => e.key === "Enter" && openLightbox(detailItem)}
									role="button"
									tabindex="0"
									title="클릭하면 전체 이미지를 크게 봅니다"
								>
									<img
										src={detailItem.images.imageUrl}
										alt={detailItem.subjectKo}
										class="position-absolute top-0 start-0 w-100 h-100"
										style="object-fit: cover;"
										on:error={handleImageError}
									/>
								</div>

								<div class="d-grid gap-2 mt-2">
									<button class="btn btn-sm btn-outline-primary" on:click={() => openLightbox(detailItem)}>
										<i class="bx bx-expand-alt me-1"></i>전체 이미지 크게 보기
									</button>
									<div class="btn-group">
										<button class="btn btn-sm btn-primary" on:click={() => openUploadModal(detailItem)}>이미지 교체</button>
										<button class="btn btn-sm btn-outline-danger" on:click={() => deleteImage(detailItem)}>삭제</button>
									</div>
								</div>

								<!-- 앱에 내려가는 세 값. 각 행을 누르면 큰 레이어로 열린다. -->
								<div class="fw-bold text-secondary small mt-3">앱에 내려가는 값</div>
								<button
									type="button"
									class="btn btn-outline-secondary text-start w-100 d-flex align-items-center gap-2 mt-1 p-2"
									on:click={() => openThumbnailLightbox(detailItem)}
								>
									<img
										src={detailItem.images.thumbnailUrl}
										alt="썸네일"
										class="border rounded flex-shrink-0"
										style="width: 64px; height: 64px; object-fit: contain; background: #f8f9fa;"
										on:error={handleImageError}
									/>
									<span class="flex-grow-1" style="min-width: 0;">
										<span class="d-block fw-bold small">썸네일</span>
										<span class="d-block text-muted" style="font-size: 0.7rem;">
											{joinMeta("512 × 512", "WebP q80", formatBytes(thumbnailSize))}
										</span>
										<span class="d-block text-muted" style="font-size: 0.7rem;">
											목록·그리드 타일에 쓰입니다
										</span>
									</span>
									<i class="bx bx-expand-alt flex-shrink-0 text-muted"></i>
								</button>

								<button
									type="button"
									class="btn btn-outline-secondary text-start w-100 d-flex align-items-center gap-2 mt-2 p-2"
									on:click={() => openLightbox(detailItem)}
								>
									<img
										src={detailItem.images.imageUrl}
										alt="전체 이미지"
										class="border rounded flex-shrink-0"
										style="width: 64px; height: 64px; object-fit: contain; background: #f8f9fa;"
										on:error={handleImageError}
									/>
									<span class="flex-grow-1" style="min-width: 0;">
										<span class="d-block fw-bold small">전체 이미지</span>
										<span class="d-block text-muted" style="font-size: 0.72rem;">
											{joinMeta(
												imageWidth > 0 && imageHeight > 0
													? `${imageWidth} × ${imageHeight}`
													: "크기 미적재 — 재업로드 필요",
												"WebP q90",
												formatBytes(imageSize)
											)}
										</span>
										<span class="d-block text-muted" style="font-size: 0.7rem;">앱이 큰 화면에 쓰입니다</span>
									</span>
									<i class="bx bx-expand-alt flex-shrink-0 text-muted"></i>
								</button>

								<button
									type="button"
									class="btn btn-outline-secondary text-start w-100 d-flex align-items-center gap-2 mt-2 p-2"
									on:click={() => openBlurHashLightbox(detailItem)}
									disabled={!blurHashPreviewUrl}
								>
									{#if blurHashPreviewUrl}
										<img
											src={blurHashPreviewUrl}
											alt="blurHash 미리보기"
											class="border rounded flex-shrink-0"
											style="width: {blurHashBoxWidth}px; height: {PREVIEW_BOX_HEIGHT}px; object-fit: fill;"
										/>
									{:else}
										<span
											class="border rounded flex-shrink-0 d-flex align-items-center justify-content-center bg-light text-muted"
											style="width: 64px; height: 64px; font-size: 0.65rem; text-align: center; line-height: 1.2;"
										>
											미리보기<br />불가
										</span>
									{/if}
									<span class="flex-grow-1" style="min-width: 0;">
										<span class="d-block fw-bold small">blurHash</span>
										<span class="d-block text-muted text-break" style="font-size: 0.68rem; font-family: monospace;">
											{detailItem.images.blurHash || "-"}
										</span>
										<span class="d-block text-muted" style="font-size: 0.7rem;">로딩 중 플레이스홀더 · 원본 비율</span>
									</span>
									<i class="bx bx-expand-alt flex-shrink-0 text-muted"></i>
								</button>

								<!--
									업로드 원본. 위 세 값과 성격이 달라 따로 묶는다.
									앱에는 내려가지 않고, 변환 규격이 바뀌었을 때 다시 뽑아낼 출처로만 보관한다.
								-->
								<div class="fw-bold text-secondary small mt-3">보관용 원본 (앱에 내려가지 않음)</div>
								<button
									type="button"
									class="btn btn-outline-secondary text-start w-100 d-flex align-items-center gap-2 mt-1 p-2"
									on:click={() => openSourceLightbox(detailItem)}
									disabled={!sourceUrl}
								>
									{#if sourceUrl}
										<img
											src={sourceUrl}
											alt="원본 이미지"
											class="border rounded flex-shrink-0"
											style="width: 64px; height: 64px; object-fit: contain; background: #f8f9fa;"
											on:error={handleImageError}
										/>
									{:else}
										<span
											class="border rounded flex-shrink-0 d-flex align-items-center justify-content-center bg-light text-muted"
											style="width: 64px; height: 64px; font-size: 0.65rem; text-align: center; line-height: 1.2;"
										>
											원본<br />없음
										</span>
									{/if}
									<span class="flex-grow-1" style="min-width: 0;">
										<span class="d-block fw-bold small">원본 이미지</span>
										<span class="d-block text-muted" style="font-size: 0.72rem;">
											{#if sourceUrl}
												{joinMeta(sourceFormat || "업로드한 파일", "변환 전", formatBytes(sourceSize))}
											{:else}
												원본 보관 도입 전 등록 — 재업로드해야 보관됩니다
											{/if}
										</span>
										<span class="d-block text-muted" style="font-size: 0.7rem;">
											변환 전 파일 그대로 · 규격이 바뀌면 여기서 다시 뽑습니다
										</span>
									</span>
									<i class="bx bx-expand-alt flex-shrink-0 text-muted"></i>
								</button>

								<div class="row g-2 mt-2">
									<div class="col-7">
										<div class="fw-bold text-secondary small mb-1">이미지 등록·교체 일시</div>
										<div class="small">{detailItem.images.updatedAt || "-"}</div>
									</div>
									<div class="col-5">
										<!-- 원파일까지 보관하면서 1건당 저장량이 크게 늘어 합계를 같이 보여준다 -->
										<div class="fw-bold text-secondary small mb-1">저장 용량 합계</div>
										<div class="small">{formatBytes(totalSize) || "미적재"}</div>
									</div>
								</div>
							{:else}
								<div
									class="rounded d-flex flex-column align-items-center justify-content-center"
									style="width: 100%; padding: 3rem 1rem; border: 2px dashed #ffab00; background: rgba(255, 171, 0, 0.08); color: #ffab00;"
								>
									<i class="bx bx-image fs-1"></i>
									<span class="small mt-2">이미지 미등록</span>
								</div>
								<div class="d-grid mt-2">
									<button class="btn btn-sm btn-primary" on:click={() => openUploadModal(detailItem)}>이미지 등록</button>
								</div>
							{/if}
						</div>

						<!-- 음원 정보 영역 -->
						<div class="col-md-7">
							{#if isDetailLoading}
								<div class="text-center text-muted py-5">
									<div class="spinner-border spinner-border-sm me-2" role="status"></div>
									음원 정보를 불러오는 중...
								</div>
							{:else if detailInfo}
								<div class="mb-3">
									<button
										class="btn btn-sm {isPlaying ? 'btn-danger' : 'btn-primary'}"
										on:click={handlePreviewAudio}
										disabled={!detailInfo.fileName}
									>
										<i class="bx {isPlaying ? 'bx-stop' : 'bx-play'} me-1"></i>
										{isPlaying ? "정지" : "음원 미리듣기"}
									</button>
								</div>

								<div class="row g-2 small">
									<div class="col-6">
										<div class="fw-bold text-secondary">한국어 제목</div>
										<div>{detailInfo.subjectKo || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">영어 제목</div>
										<div>{detailInfo.subjectEn || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">일본어 제목</div>
										<div>{detailInfo.subjectJa || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">중국어 간체 제목</div>
										<div>{detailInfo.subjectZhCn || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">중국어 번체 제목</div>
										<div>{detailInfo.subjectZhTw || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">컨텐츠장르</div>
										<div>{detailInfo.categoryNameKo || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">심박수 레벨</div>
										<div>
											<span class="badge {getHRLevelBadgeColor(detailInfo.level)}">
												{getHRLevelText(detailInfo.level) || "-"}
											</span>
										</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">노출 상태</div>
										<div>
											<span class="badge {detailInfo.isActive ? 'bg-success' : 'bg-secondary'}">
												{detailInfo.isActive ? "노출" : "미노출"}
											</span>
										</div>
									</div>
									<div class="col-12">
										<div class="fw-bold text-secondary">원본 음원</div>
										<div>{detailInfo.sourceSubject || "-"}</div>
									</div>
									<div class="col-12">
										<div class="fw-bold text-secondary">원본 음원 파일</div>
										<div class="text-break">{detailInfo.fileName || "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">등록일</div>
										<div>{detailInfo.createdAt ? Dates.defaultConvertFull(detailInfo.createdAt) : "-"}</div>
									</div>
									<div class="col-6">
										<div class="fw-bold text-secondary">수정일</div>
										<div>{detailInfo.updatedAt ? Dates.defaultConvertFull(detailInfo.updatedAt) : "-"}</div>
									</div>
								</div>

								<div class="alert alert-secondary small mt-3 mb-0">
									제목 · 장르 · 노출 상태는 <strong>컨텐츠 음원 (2세대)</strong> 화면에서 수정합니다. 이 화면은 이미지만 다룹니다.
								</div>
							{:else}
								<div class="text-center text-muted py-5">음원 정보를 불러오지 못했습니다.</div>
							{/if}
						</div>
					</div>

					<!-- 피드백 스레드 — 좌/우 2단이 이미 꽉 차서 하단 전체폭으로 둔다 -->
					{#if detailItem}
						<div class="mt-4">
							<SoundImageFeedback idSound={detailItem.id} on:countChange={handleFeedbackCountChange} />
						</div>
					{/if}
				</div>

				<div class="modal-footer">
					<button type="button" class="btn btn-outline-secondary" on:click={closeDetailModal}>닫기</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!--
	이미지 원본보기.

	⚠️ 이 아래 네 오버레이(상세보기 / 라이트박스 / 음원선택 / 업로드)는 z-index 를 직접 주지 않는다.
	이 테마의 core.css 는 .modal 을 z-index 1090 으로 정의한다 (부트스트랩 기본값 1055 가 아니다).
	직접 준 값이 1090 보다 낮으면 상세보기 뒤로 깔려버리고, 높이면 테마의 tooltip(1099)·
	toast(1095) 위로 튀어나온다. 같은 z-index 끼리는 DOM 순서가 뒤인 쪽이 위에 그려지므로
	상세보기 → 라이트박스 → 음원선택 → 업로드 순서만 지키면 된다. 블록 순서를 바꾸지 말 것.
-->
{#if lightboxUrl}
	<div
		class="modal fade show d-block"
		tabindex="-1"
		style="background: rgba(0, 0, 0, 0.85);"
		transition:fade={{ duration: 120 }}
		on:click={closeLightbox}
		on:keydown={(e) => e.key === "Escape" && closeLightbox()}
		role="presentation"
	>
		<div class="modal-dialog modal-dialog-centered" style="max-width: 96vw;">
			<div class="modal-content bg-transparent border-0">
				<div class="d-flex justify-content-between align-items-center mb-2 text-white">
					<span class="fw-semibold text-truncate">{lightboxTitle}</span>
					<div class="d-flex gap-2 flex-shrink-0">
						<button
							class="btn btn-sm btn-light"
							on:click|stopPropagation={() => (lightboxIsActualSize = !lightboxIsActualSize)}
						>
							<i class="bx {lightboxIsActualSize ? 'bx-zoom-out' : 'bx-zoom-in'} me-1"></i>
							{lightboxIsActualSize ? "화면에 맞추기" : "원본 크기"}
						</button>
						{#if lightboxHasSource}
							<a
								class="btn btn-sm btn-light"
								href={lightboxUrl}
								target="_blank"
								rel="noopener noreferrer"
								on:click|stopPropagation
							>
								<i class="bx bx-link-external me-1"></i>새 탭에서 열기
							</a>
						{/if}
						<button class="btn btn-sm btn-light" on:click|stopPropagation={closeLightbox}>닫기</button>
					</div>
				</div>
				<!--
					원본 크기일 때는 이미지를 줄이지 않고 이 상자 안에서 스크롤한다.
					가운데 정렬을 justify-content 로 하면 넘칠 때 위·왼쪽이 잘려 스크롤로도 못 가므로
					자식에 margin:auto 를 줘서 정렬한다.
				-->
				<div
					class="rounded d-flex"
					style="max-height: 78vh; overflow: auto; background: rgba(0, 0, 0, 0.35);"
					on:click|stopPropagation
					role="presentation"
				>
					<img
						src={lightboxUrl}
						alt={lightboxTitle}
						class="rounded"
						style={lightboxIsActualSize
							? "margin: auto; max-width: none; flex-shrink: 0;"
							: "margin: auto; max-width: 100%; max-height: 78vh; object-fit: contain;"}
						on:error={handleImageError}
					/>
				</div>
				<div class="text-center text-white-50 small mt-2 text-break">
					{lightboxCaption} · {lightboxIsActualSize ? "원본 크기(100%)" : "화면 맞춤"} · 배경을 클릭하면 닫힙니다
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- 업로드 모달 -->
{#if showUploadModal}
	<div class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5);" transition:fade={{ duration: 120 }}>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">음원 이미지 {uploadTarget?.images ? "교체" : "등록"}</h5>
					<button type="button" class="btn-close" on:click={closeUploadModal} aria-label="닫기"></button>
				</div>

				<div class="modal-body">
					<div class="mb-3">
						<div class="small text-muted">대상 음원</div>
						<div class="fw-semibold">
							{uploadTarget?.subjectKo}
							<span class="text-muted">#{uploadTarget?.id}</span>
						</div>
					</div>

					<div class="d-flex align-items-start gap-3">
						<div
							class="border rounded overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center bg-light"
							style="width: 140px; height: 140px;"
						>
							{#if imagePreviewUrl}
								<img src={imagePreviewUrl} alt="선택한 이미지 미리보기" class="w-100 h-100" style="object-fit: cover;" />
							{:else if uploadTarget?.images?.thumbnailUrl}
								<img
									src={uploadTarget.images.thumbnailUrl}
									alt="현재 등록된 이미지"
									class="w-100 h-100"
									style="object-fit: cover;"
									on:error={handleImageError}
								/>
							{:else}
								<i class="bx bx-image fs-1 text-muted"></i>
							{/if}
						</div>

						<div class="flex-grow-1">
							<input
								type="file"
								class="form-control"
								accept="image/png,image/jpeg,image/webp"
								on:change={handleImageSelect}
							/>
							<div class="form-text">
								PNG · JPEG · WebP. 업로드한 파일은 원본 그대로 보관하고, 서버가 전체 이미지(WebP)·썸네일·blurHash 를 생성합니다.
							</div>
						</div>
					</div>
				</div>

				<div class="modal-footer">
					<button type="button" class="btn btn-outline-secondary" on:click={closeUploadModal}>취소</button>
					<button type="button" class="btn btn-primary" on:click={uploadImage} disabled={isSaving || !selectedImageFile}>
						{isSaving ? "업로드 중..." : "저장"}
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}
